import React, { useContext, useEffect, useState } from 'react'

import type { PopupMenuProps, StyleClasses, MuiTheme as Theme } from '@paintscout/ui'
import { makeStyles, Tooltip, Button, Grid, MenuItem, PopupMenu, useMediaQuery } from '@paintscout/ui'

import { CLOSED_STATUSES, getObjectLabels, getQuoteOptions, hasIntegrationInterface } from '@paintscout/util/builder'

import CheckIcon from '@material-ui/icons/Check'

import { usePresentationNav } from '../PresentationNavContext'
import { getPresentationButtonColors } from '../../util'
import { usePresentationMoreActions } from './usePresentationMoreActions'
// imported straight from the dialog, not the dialogs barrel — the barrel pulls the whole
// presentation editor in behind it, and this component renders in the customer view
import PresentationTermsDialog from '../../dialogs/PresentationTermsDialog'

import { QuoteContext } from '../../../context/QuoteContext'
import { useClientOptions, useUser } from '@paintscout/ui-business'

interface PresentationAcceptButtonStyleProps extends PresentationAcceptButtonProps {
  /** presentation accent colour — see getPresentationButtonColors */
  buttonBackgroundColor?: string
  /** contrast text for buttonBackgroundColor */
  buttonColor?: string
}

const useStyles = makeStyles<Theme, PresentationAcceptButtonStyleProps>(
  (theme: Theme) => {
    return {
      root: {},
      acceptGridRoot: {
        height: '100%',
        padding: theme.spacing(1, 0)
      },
      // the presentation theme never reaches the nav (it only becomes CSS for the content
      // subtree), so the accent colour is handed in as a prop instead
      button: ({ buttonBackgroundColor, buttonColor }: PresentationAcceptButtonStyleProps) => ({
        color: buttonColor,
        backgroundColor: buttonBackgroundColor,

        [theme.breakpoints.up('lg')]: {
          textAlign: 'center'
        },
        '&:hover': {
          backgroundColor: buttonBackgroundColor,
          color: buttonColor,
          opacity: 0.8
        }
      }),
      // The phone nav bar's Accept, as the prototype draws it (ENG-2075): a fixed blue 36px button
      // with a check icon, rather than the presentation's accent colour.
      compactButton: {
        height: 36,
        minWidth: 0,
        padding: theme.spacing(0, 1.75),
        borderRadius: 6,
        boxShadow: 'none',
        color: theme.paintscout.palette.brand.Neutral.White,
        backgroundColor: '#2358ee',
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        lineHeight: '20px',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        '&:hover, &:active': {
          backgroundColor: '#1c4bd4',
          boxShadow: 'none'
        }
      },
      compactIcon: {
        fontSize: 16,
        marginRight: 6
      },
      oversizeSplitButton: {
        padding: theme.spacing(2)
      },
      moreButton: {
        marginLeft: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
          marginLeft: theme.spacing(2)
        },
        [theme.breakpoints.down('xs')]: {
          marginLeft: theme.spacing(1)
        }
      },
      moreMenu: {
        ['@media print']: {
          display: 'none' // for printing from PresentationQuoteDrawer
        }
      },
      tooltip: {
        '& div': {
          fontSize: '.875rem'
        }
      }
    }
  },
  { name: 'PresentationAcceptButton' }
)

export interface PresentationAcceptButtonProps {
  classes?: StyleClasses<typeof useStyles>
  anchorOrigin?: PopupMenuProps['anchorOrigin']
  transformOrigin?: PopupMenuProps['transformOrigin']
  /**
   * The phone nav bar's form (ENG-2075): the prototype's blue Accept with a check icon, and no
   * "More…" menu — on the customer view its items move to the bottom of the Pages drawer.
   */
  compact?: boolean
}

export default function PresentationAcceptButton(props: PresentationAcceptButtonProps) {
  const { anchorOrigin, transformOrigin, compact } = props

  const { options } = useClientOptions()

  const { view, isResponding, allowResponse, onAcceptClick, onPayClick, onMoreClick, isPreview, onCloseCart } =
    usePresentationNav()
  const { quote } = useContext(QuoteContext)
  const { preferences } = useUser()

  const { allowPaymentsOnInvoices } = getQuoteOptions({ quote, options })

  // Stripe-only here missed Stripe W1D and Payrix accounts entirely, hiding Pay Now/Pay for any
  // account on either of those gateways (#22832) — the v1 PresentationAcceptButton already checks
  // all three.
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments = (hasStripe || hasStripeW1d || hasPayrix) && !!onPayClick

  const allowPayment =
    hasPayments && allowPaymentsOnInvoices && quote.is_invoice && view !== 'work-order' && view !== 'product-order-form'

  const showAcceptButton =
    view !== 'work-order' && (allowResponse || isPreview) && !!onAcceptClick && !quote.is_invoice && !allowPayment

  const {
    actions: moreActions,
    hasTerms,
    termsOpen,
    closeTerms,
    hasOpenStatus,
    presentation
  } = usePresentationMoreActions()

  const objectLabels = getObjectLabels({ options, quote })

  const smDown = useMediaQuery('sm')
  const xsDown = useMediaQuery('xs')

  const buttonLabel = getButtonLabel({ objectLabels, smDown })

  const hasMoreButton = !!onMoreClick && !compact
  const moreButtonLabel = getMoreButtonLabel()

  const { backgroundColor, color } = getPresentationButtonColors(presentation?.advanced?.style)
  const classes = useStyles({ ...props, buttonBackgroundColor: backgroundColor, buttonColor: color })
  const buttonClasses = { root: compact ? classes.compactButton : classes.button }

  const handleAcceptClick = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onAcceptClick(ev)
  }

  const [initStatus, _setInitStatus] = useState(quote?.status?.value)

  useEffect(() => {
    if (!CLOSED_STATUSES.includes(initStatus) && !hasOpenStatus) {
      onCloseCart()
    }
  }, [quote?.status?.value])

  const moreButton = (
    <Grid item>
      <PopupMenu
        className={classes.moreMenu}
        // #23652: PresentationImbedPreview renders this nav inside a <Frame> (react-frame-component)
        // iframe. MUI's Menu portals its Paper into document.body by default — in an iframe context
        // that's a document boundary the click-away/backdrop click detection doesn't cleanly cross,
        // so the menu appeared to escape the frame and a stray click after closing it landed on the
        // first item. Rendering inline (no portal) keeps everything in the iframe's own document.
        disablePortal={isPreview}
        anchorOrigin={
          anchorOrigin || {
            vertical: 'top',
            horizontal: 'left'
          }
        }
        transformOrigin={
          transformOrigin || {
            vertical: 'bottom',
            horizontal: 'left'
          }
        }
        component={
          <Button variant="text" className={classes.moreButton}>
            {moreButtonLabel}
          </Button>
        }
      >
        <>
          {moreActions.map(({ key, label, icon, onSelect }) => (
            <MenuItem key={key} icon={icon} onClick={onSelect}>
              {label}
            </MenuItem>
          ))}
        </>
      </PopupMenu>
    </Grid>
  )

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={1}
        wrap={'nowrap'}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        classes={{ container: classes.acceptGridRoot }}
      >
        {allowPayment && (
          <Grid item>
            <Tooltip
              classes={{ popper: classes.tooltip }}
              title={
                quote.totals.balance_due > 0 && quote.totals.balance_due < 0.5
                  ? 'Payments are only available if the remaining balance is at least $0.50 '
                  : ''
              }
            >
              <div>
                <Button
                  classes={buttonClasses}
                  disabled={quote.totals.balance_due < 0.5}
                  loading={isResponding}
                  onClick={() => onPayClick()}
                >
                  {getPaymentButtonLabel()}
                </Button>
              </div>
            </Tooltip>
          </Grid>
        )}
        {showAcceptButton && (
          <Grid item>
            <Button
              loading={isResponding}
              disabled={!allowResponse || !hasOpenStatus}
              classes={buttonClasses}
              onClick={(ev) => handleAcceptClick(ev)}
            >
              {compact && <CheckIcon className={classes.compactIcon} />}
              {buttonLabel}
            </Button>
          </Grid>
        )}
        {/* independent of showAcceptButton/allowPayment so invoices without payments enabled still
            get Print/Download (#23652) */}
        {hasMoreButton && moreButton}
      </Grid>
      {hasTerms && <PresentationTermsDialog open={termsOpen} onClose={closeTerms} />}
    </div>
  )

  function getMoreButtonLabel() {
    if (xsDown) return 'More...'
    return 'More Actions...'
  }

  function getPaymentButtonLabel() {
    if (xsDown) return quote.totals.balance_due === 0 && quote.payments.length > 0 ? 'Paid' : 'Pay'
    return quote.totals.balance_due === 0
      ? quote.payments.length === 0
        ? 'No Balance Due'
        : 'Payment Received'
      : quote.totals.balance_due < 200
        ? 'Pay Balance'
        : 'Make Payment'
  }

  function getButtonLabel({ objectLabels, smDown }) {
    if (objectLabels.acceptButton) {
      return objectLabels.acceptButton.value
    } else if (smDown) {
      return 'Accept'
    } else {
      const item = objectLabels.quote.value
      return `Accept ${item}`
    }
  }
}
